/** Header for non-mission pages (missions list, new, join). */
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { BrandMark, Icon, MenuItem, Popover } from '../components/ui';
import { useServerNow } from '../lib/clock';
import { fmt } from '../lib/fmt';
import { usePrefs } from '../lib/prefs';
import { useSession } from '../state/session';

export function AppHeader({ back }: { back?: { to: string; label: string } }) {
  const user = useSession((s) => s.user);
  const logout = useSession((s) => s.logout);
  const theme = usePrefs((s) => s.theme);
  const toggleTheme = usePrefs((s) => s.toggleTheme);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const now = useServerNow(1000);

  return (
    <header className="app-header">
      <div className="app-header__left">
        {back ? (
          <Link to={back.to} className="app-header__back" aria-label={back.label}>
            <Icon name="arrow-left" size={18} />
          </Link>
        ) : null}
        <Link to="/missions" className="brand-lockup" aria-label="Airview Studio — shoots">
          <BrandMark size={22} />
          <span className="brand-lockup__word">
            AIRVIEW STUDIO
          </span>
        </Link>
      </div>
      <div className="app-header__right">
        <span className="app-header__clock" title="Server UTC">
          <span className="app-header__clock-tz">UTC</span>
          <span className="num">{fmt.utc(now).replace(/Z$/, '')}</span>
        </span>
        <button ref={anchor} type="button" className="user-chip" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}>
          <span className="user-chip__avatar">{(user?.displayName ?? '?').slice(0, 1).toUpperCase()}</span>
          <span className="user-chip__name">{user?.displayName}</span>
          <Icon name="chevron-down" size={14} />
        </button>
        <Popover open={open} onClose={() => setOpen(false)} anchor={anchor}>
          <div className="menu-label">
            <div className="eyebrow">Signed in</div>
            <div className="menu-user">{user?.email}</div>
          </div>
          <div className="menu-sep" />
          <MenuItem icon="aircraft" onClick={() => { setOpen(false); nav('/fleets'); }}>Fleets</MenuItem>
          <MenuItem icon="camera" onClick={() => { setOpen(false); nav('/cameras'); }}>Cameras & ATC</MenuItem>
          <MenuItem icon="target" onClick={() => { setOpen(false); nav('/shot-planner'); }}>Shot planner</MenuItem>
          <MenuItem icon="team" onClick={() => { setOpen(false); nav('/organization'); }}>Organization</MenuItem>
          <MenuItem icon="settings" onClick={() => { setOpen(false); nav('/settings'); }}>Settings</MenuItem>
          <MenuItem icon="flag" onClick={() => { setOpen(false); nav('/support'); }}>Support & reporting</MenuItem>
          <MenuItem icon={theme === 'dark' ? 'sun' : 'moon'} onClick={toggleTheme}>
            {theme === 'dark' ? 'Daylight theme' : 'Dark theme'}
          </MenuItem>
          <MenuItem
            icon="logout"
            onClick={async () => {
              setOpen(false);
              await logout();
              nav('/login');
            }}
          >
            Sign out
          </MenuItem>
        </Popover>
      </div>
    </header>
  );
}
